const CSRF_SYNC_TIMEOUT_MS = 10000;
const CSRF_SYNC_BURST_ATTEMPTS = 2;
const CSRF_SYNC_RETRY_DELAYS_MS = [1000, 3000, 10000, 30000];

const applyCsrfToken = (token) => {
    if (!token) {
        return '';
    }

    let meta = document.head.querySelector('meta[name="csrf-token"]');

    if (!meta) {
        meta = document.createElement('meta');
        meta.name = 'csrf-token';
        meta.id = 'csrf-token';
        document.head.appendChild(meta);
    }

    meta.setAttribute('content', token);

    document.querySelectorAll('input[name="_token"]').forEach((input) => {
        input.value = token;
    });

    window.livewireScriptConfig = window.livewireScriptConfig || {};
    window.livewireScriptConfig.csrf = token;

    return token;
};

const requestCsrfToken = async () => {
    let lastError = null;

    for (let attempt = 1; attempt <= CSRF_SYNC_BURST_ATTEMPTS; attempt++) {
        const controller = new AbortController();
        const timeoutId = window.setTimeout(() => controller.abort(), CSRF_SYNC_TIMEOUT_MS);

        try {
            const response = await fetch('/csrf', {
                credentials: 'same-origin',
                headers: {
                    'X-Requested-With': 'XMLHttpRequest',
                },
                signal: controller.signal,
            });

            if (!response.ok) {
                throw new Error(`CSRF token request failed with status ${response.status}`);
            }

            const data = await response.json();
            const token = applyCsrfToken(data?.token || '');

            if (token) {
                return { token, error: null };
            }

            throw new Error('CSRF token response did not contain a token');
        } catch (error) {
            lastError = error?.name === 'AbortError'
                ? `timed out after ${CSRF_SYNC_TIMEOUT_MS} ms`
                : error;
        } finally {
            window.clearTimeout(timeoutId);
        }
    }

    return { token: '', error: lastError };
};

const ensureCsrfTokenResult = async () => {
    if (!window.__vsebankiCsrfTokenPromise) {
        window.__vsebankiCsrfTokenPromise = requestCsrfToken();
    }

    const result = await window.__vsebankiCsrfTokenPromise;

    if (!result.token) {
        window.__vsebankiCsrfTokenPromise = null;
    }

    return result;
};

const ensureCsrfToken = async () => (await ensureCsrfTokenResult()).token;

const waitForDomReady = () => {
    if (document.readyState !== 'loading') {
        return Promise.resolve();
    }

    return new Promise((resolve) => {
        document.addEventListener('DOMContentLoaded', resolve, { once: true });
    });
};

const waitForCsrfRetry = (delay) => new Promise((resolve) => {
    window.setTimeout(resolve, delay);
});

const canRetryCsrfSync = ({ allowHiddenPage = false } = {}) => {
    const pageIsVisible = allowHiddenPage || document.visibilityState !== 'hidden';
    const browserIsOnline = window.navigator?.onLine !== false;

    return pageIsVisible && browserIsOnline;
};

const waitForCsrfRetryAvailability = (options) => {
    if (canRetryCsrfSync(options)) {
        return Promise.resolve(false);
    }

    return new Promise((resolve) => {
        const resume = () => {
            if (!canRetryCsrfSync(options)) {
                return;
            }

            window.removeEventListener('online', resume);
            document.removeEventListener('visibilitychange', resume);
            resolve(true);
        };

        window.addEventListener('online', resume);
        document.addEventListener('visibilitychange', resume);
    });
};

const waitForCsrfToken = async () => {
    let retryIndex = 0;
    let firstRequest = true;

    while (!window.__vsebankiLivewireStarted) {
        await waitForCsrfRetryAvailability({ allowHiddenPage: firstRequest });

        const { token, error } = await ensureCsrfTokenResult();

        if (token) {
            return token;
        }

        const delay = CSRF_SYNC_RETRY_DELAYS_MS[Math.min(retryIndex, CSRF_SYNC_RETRY_DELAYS_MS.length - 1)];

        console.warn(
            `CSRF token sync failed after ${CSRF_SYNC_BURST_ATTEMPTS} attempts. Retrying in ${delay} ms when the page is visible and online.`,
            error,
        );

        retryIndex += 1;
        firstRequest = false;
        const resumedAfterPause = await waitForCsrfRetryAvailability();

        if (!resumedAfterPause) {
            await waitForCsrfRetry(delay);
        }
    }

    return '';
};

const startLivewire = () => {
    if (window.__vsebankiLivewireStarted || window.__vsebankiLivewireStarting) {
        return;
    }

    if (!window.Livewire || typeof window.Livewire.start !== 'function') {
        console.error('Livewire is not available for manual start.');
        return;
    }

    window.__vsebankiLivewireStarting = true;

    try {
        window.Livewire.start();
        window.__vsebankiLivewireStarted = true;
    } catch (error) {
        console.error('Failed to start Livewire manually:', error);
    } finally {
        window.__vsebankiLivewireStarting = false;
    }
};

const scheduleLivewireStart = () => {
    if (window.__vsebankiLivewireStartScheduled) {
        return;
    }

    window.__vsebankiLivewireStartScheduled = true;

    Promise.all([waitForDomReady(), waitForCsrfToken()])
        .then(([, token]) => {
            if (!token) {
                return;
            }

            startLivewire();
        })
        .catch((error) => {
            console.error('Failed to prepare Livewire manual start:', error);
        });
};

window.__vsebankiApplyCsrfToken = window.__vsebankiApplyCsrfToken || applyCsrfToken;
window.__vsebankiEnsureCsrfToken = window.__vsebankiEnsureCsrfToken || ensureCsrfToken;

scheduleLivewireStart();
